import { useEffect, useState } from "react";
import { Sparkles, Check, RefreshCw, X } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

import { tr } from "../lib/i18n";
/**
 * "Improve with AI" button that shows a side-by-side diff preview.
 * The user MUST accept before we overwrite the description — never auto-apply.
 * If AI is disabled server-side the button is hidden.
 */
export default function AiRewriteButton({ text, vehicleId, onAccept, structuredFields }) {
  const [status, setStatus] = useState({ enabled: false, on_cap: false });
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [tone, setTone] = useState("professional");
  const [result, setResult] = useState("");
  const [mode, setMode] = useState("rewrite"); // rewrite | generate

  useEffect(() => {
    api.get("/ai/status")
      .then(({ data }) => setStatus({ enabled: !!data.enabled, on_cap: !!data.on_cap }))
      .catch(() => setStatus({ enabled: false, on_cap: false }));
  }, []);

  if (!status.enabled) return null;

  const call = async () => {
    setBusy(true);
    setResult("");
    try {
      if (mode === "generate") {
        const { data } = await api.post("/ai/generate-description", {
          fields: structuredFields || {},
          vehicle_id: vehicleId,
        });
        setResult(data.generated);
      } else {
        const { data } = await api.post("/ai/rewrite-description", {
          text: text || "",
          tone,
          vehicle_id: vehicleId,
        });
        setResult(data.rewritten);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const openWith = (m) => {
    setMode(m);
    setResult("");
    setOpen(true);
    // Fire the call after paint so the modal opens instantly.
    setTimeout(call, 0);
  };

  const accept = () => {
    if (result) {
      onAccept?.(result);
      toast.success(tr("x.ai_suggestion_applied"));
    }
    setOpen(false);
  };

  const canRewrite = (text || "").trim().length >= 10;
  const canGenerate = !!structuredFields && Object.values(structuredFields).some((v) => v);

  return (
    <>
      <div className="flex gap-2 flex-wrap">
        <button
          type="button"
          data-testid="ai-rewrite-btn"
          disabled={!canRewrite}
          onClick={() => openWith("rewrite")}
          className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-[hsl(var(--border))] text-xs font-semibold hover:border-[var(--accent)] hover:text-[var(--accent)] disabled:opacity-40 transition-colors"
        >
          <Sparkles size={13} /> {tr("x.improve_with_ai")}
        </button>
        {canGenerate && (
          <button
            type="button"
            data-testid="ai-generate-btn"
            onClick={() => openWith("generate")}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-[hsl(var(--border))] text-xs font-semibold hover:border-[var(--accent)] hover:text-[var(--accent)] transition-colors"
          >
            <Sparkles size={13} /> {tr("x.draft_from_specs")}
          </button>
        )}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="panel p-5 w-full max-w-4xl max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="ai-preview-modal">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="eyebrow">{tr("x.ai_suggestion")} {mode === "generate" ? tr("x.draft_from_specs_2") : "rewrite"}</div>
                <h3 className="text-lg font-semibold mt-0.5" style={{ fontFamily: "Outfit" }}>
                  {tr("x.review_before_applying")}
                </h3>
              </div>
              <button onClick={() => setOpen(false)} className="p-1 hover:bg-[hsl(var(--muted))] rounded"><X size={16} /></button>
            </div>

            {mode === "rewrite" && (
              <div className="mb-3 flex gap-2 flex-wrap">
                {["professional", "concise", "sales", "fix_grammar_only"].map((t) => (
                  <button
                    key={t}
                    data-testid={`tone-${t}`}
                    onClick={() => { setTone(t); setTimeout(call, 0); }}
                    className={`h-7 px-3 rounded-full text-xs font-semibold border ${tone === t ? "bg-accent-brand text-white border-[var(--accent)]" : "border-[hsl(var(--border))]"}`}
                  >
                    {t.replace("_", " ")}
                  </button>
                ))}
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-3">
              {mode === "rewrite" && (
                <div>
                  <div className="eyebrow mb-2">{tr("x.original")}</div>
                  <div className="panel p-3 text-sm whitespace-pre-wrap max-h-96 overflow-y-auto">{text || "—"}</div>
                </div>
              )}
              <div className={mode === "rewrite" ? "" : "md:col-span-2"}>
                <div className="eyebrow mb-2 flex items-center justify-between">
                  <span>{tr("x.suggestion")}</span>
                  {busy && <span className="text-[var(--gray-400)] normal-case tracking-normal text-xs">{tr("x.thinking")}</span>}
                </div>
                <div className="panel p-3 text-sm whitespace-pre-wrap max-h-96 overflow-y-auto border-[var(--accent)]/40">
                  {busy ? <span className="text-[var(--gray-400)]">{tr("x.generating")}</span> : (result || <span className="text-[var(--gray-400)]">{tr("x.no_suggestion_yet")}</span>)}
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 justify-end mt-4">
              <button onClick={() => setOpen(false)} className="h-9 px-4 rounded-lg border border-[hsl(var(--border))] text-sm">{tr("x.discard")}</button>
              <button onClick={call} disabled={busy} data-testid="ai-regenerate" className="h-9 px-4 rounded-lg border border-[hsl(var(--border))] text-sm flex items-center gap-1.5">
                <RefreshCw size={14} /> {tr("x.regenerate")}
              </button>
              <button onClick={accept} disabled={!result || busy} data-testid="ai-accept" className="h-9 px-4 rounded-lg bg-accent-brand text-white text-sm font-semibold flex items-center gap-1.5">
                <Check size={14} /> {tr("x.accept_apply")}
              </button>
            </div>

            <p className="text-[11px] text-[var(--gray-400)] mt-3">
              {tr("x.ai_never_overwrites_your_text_automatica")}
            </p>
          </div>
        </div>
      )}
    </>
  );
}
